import {
  Alert,
  Sentiments,
  Sizes,
} from '@sede-enterprise/shell-ds-react-framework';
import { useAppUserDetailsFromJwt } from 'common-react/auth/utils/hooks/useAppUserDetailsFromJwt/useAppUserDetailsFromJwt';
import { CompanyListDataNode } from 'components/CompanyList/CompanyList.types';
import { CompanySelectorLandingContainer } from 'components/NavBar/CompanySelectorLandingContainer';
import CompanySelectorModal from 'components/NavBar/components/CompanySelector/CompanySelectorModal/CompanySelectorModal';
import { GET_COMPANIES } from 'components/NavBar/components/CompanySelector/CompanySelectorModal/queries';
import { SiteSelectorMobile } from 'components/NavBar/components/SiteSelectorMobile/SiteSelectorMobile';
import { getDefaultPathForUser, NavBar } from 'components/NavBar/NavBar';
import { GET_COMPANY } from 'components/NavBar/queries';
import { Permissions } from 'context/constants';
import useCheckUserConnection from 'hooks/useConnection/useConnection';
import { useDimensions } from 'hooks/useDimensions/useDimensions';
import { usePlutoApiQuery } from 'hooks/useQuery/plutoApi';
import { MobileCompanySiteDrawer } from 'pages/Optimisation/components/mobile/MobileCompanySiteDrawer';
import { MobileProfileDrawer } from 'pages/Optimisation/components/mobile/MobileProfileDrawer';
import { useEffect, useRef } from 'react';
import { Outlet, useLocation, useNavigate, useParams } from 'react-router';
import { COMPANY_SELECTOR_PATH, ROOT_PATH } from 'router/AppRoutes';
import { AppContainerStyled } from 'router/AppRoutes.styled.ts';
import { MobileFooter } from 'router/MobileFooter';
import { useStore } from 'store';
import {
  checkAvailabilityEnabled,
  checkOperationsEnabled,
} from 'utils/features';

const useRedirectToDefaultRoute = (companies: { node: { id: string } }[]) => {
  const location = useLocation();
  const navigate = useNavigate();
  const pendingRedirect = useRef<string | undefined>(undefined);

  const { permissions } = useAppUserDetailsFromJwt<Permissions>();
  const defaultPath = getDefaultPathForUser(permissions);

  useEffect(() => {
    const userHasASingleCompany = companies.length === 1;
    let redirectPath: string | undefined;

    if (
      location.pathname === ROOT_PATH ||
      location.pathname === COMPANY_SELECTOR_PATH
    ) {
      if (userHasASingleCompany) {
        const firstCompanyId = companies[0].node.id;
        redirectPath = `/${firstCompanyId}/${defaultPath}`;
      } else if (
        companies.length > 1 &&
        location.pathname !== COMPANY_SELECTOR_PATH
      ) {
        redirectPath = COMPANY_SELECTOR_PATH;
      }
    }

    if (!redirectPath) {
      pendingRedirect.current = undefined;
    } else if (pendingRedirect.current !== redirectPath) {
      pendingRedirect.current = redirectPath;
      navigate(redirectPath, { replace: true });
    }
  }, [companies, defaultPath, location.pathname, navigate]);
};

const AppContainer = () => {
  const { company, site: selectedSite } = useParams();

  const hideOperations = !checkOperationsEnabled();
  const hideAvailability = !checkAvailabilityEnabled();
  const hideNavBar = hideOperations && hideAvailability;

  const { isMobileOrTablet } = useDimensions();

  const [
    companyModalOpen,
    mobileCompanySiteDrawerOpen,
    isUserInternetOnline,
    siteModalOpen,
    setSiteModalClosed,
    mobileProfileOpen,
    setCompanyData,
  ] = useStore((state) => [
    state.companyModalOpen,
    state.mobileCompanySiteDrawerOpen,
    state.isUserInternetOnline,
    state.siteModalOpen,
    state.setSiteModalClosed,
    state.mobileProfileOpen,
    state.setCompanyData,
  ]);

  useCheckUserConnection();

  const { loading, data: companiesData } = usePlutoApiQuery(GET_COMPANIES, {
    variables: {
      filter: { name: { iLike: '%%' } },
    },
  });

  const { data: companyData, error: companyError } = usePlutoApiQuery(
    GET_COMPANY,
    {
      skip: !company,
      variables: { id: company },
    },
  );

  const userHasMultipleCompanies =
    (companiesData?.companies?.edges?.length ?? 0) > 1;
  const companyHasMultipleSites =
    (companyData?.company?.sites?.length ?? 0) > 1;

  const selectedCompanyData = companiesData?.companies?.edges.find(
    (edge: CompanyListDataNode) => edge.node.id === company,
  );

  useEffect(() => {
    setCompanyData(selectedCompanyData);
  }, [selectedCompanyData]);

  useRedirectToDefaultRoute(companiesData?.companies?.edges ?? []);

  return (
    <AppContainerStyled>
      {!isMobileOrTablet ? (
        <NavBar
          multipleCompanies={userHasMultipleCompanies}
          companyName={companyData?.company.name}
          companyError={companyError}
        />
      ) : null}
      {!isUserInternetOnline && (
        <Alert
          data-testid="loss-connection-banner"
          elevation
          sentiment={Sentiments.Warning}
          solidBgColor
          size={Sizes.Medium}
          style={{
            width: '100%',
            paddingRight: '25px',
            zIndex: '1',
            border: 'none',
            position: 'relative',
          }}
        >
          Data is no longer live, please check your internet connection
        </Alert>
      )}
      {loading && <CompanySelectorLandingContainer />}
      {!loading && <Outlet />}
      {isMobileOrTablet && !hideNavBar ? <MobileFooter /> : null}
      {userHasMultipleCompanies && (!company || companyModalOpen) ? (
        <CompanySelectorModal />
      ) : null}
      {isMobileOrTablet && siteModalOpen ? (
        <SiteSelectorMobile
          closeModal={setSiteModalClosed}
          sites={companyData?.company?.sites}
          selectedSite={selectedSite}
        />
      ) : null}
      {mobileCompanySiteDrawerOpen && isMobileOrTablet ? (
        <MobileCompanySiteDrawer
          companyName={companyData?.company.name}
          companyHasMultipleSites={companyHasMultipleSites}
        />
      ) : null}
      {mobileProfileOpen && isMobileOrTablet ? <MobileProfileDrawer /> : null}
    </AppContainerStyled>
  );
};

export default AppContainer;
